<template>
    <ui-container class="py-8 space-y-8" v-if="toolboxTalk">
        <!-- Header. -->
        <ui-header :title="`Toolbox Talk ${toolboxTalk.formattedDate}`" :subtitle="'ID #'+this.toolboxTalk.id">
            <template #actions>
                <ui-dropdown placement="bottom-end" menu-class="p-3 w-64">
                    <template #default="{ toggle }">
                        <ui-button-new @click="toggle" primary>
                            Actions
                            <i class="flex-shrink-0 ml-2 fa fa-chevron-down"></i>
                        </ui-button-new>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-calculator"
                                          @click="edit()">
                            Edit
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!toolboxTalk.locked"></ui-dropdown-divider>
                        <ui-dropdown-item v-if="!toolboxTalk.locked" danger icon="fa fa-trash"
                                          @click="openDeleteModal">Delete
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </template>
        </ui-header>
        <ui-data-grid>
            <ui-data-grid-item label="Toolbox Talk Date" :value="toolboxTalk.formattedDate"></ui-data-grid-item>
            <ui-data-grid-item label="Toolbox Talk Reference" :value="toolboxTalk.reference"></ui-data-grid-item>
            <ui-data-grid-item wide label="Toolbox Talk Title" :value="toolboxTalk.title"></ui-data-grid-item>
            <ui-data-grid-item wide label="Toolbox Talk" :value="toolboxTalk.talk"></ui-data-grid-item>
        </ui-data-grid>
        <div class="space-y-0">
            <label class="py-0">Attendees</label>
            <div class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                <div class="w-6 flex-shrink-0"></div>
                <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Name</div>
                <div class="flex-1 border-r border-gray-200 px-2 py-1">Position</div>
                <div class="w-20 border-r border-gray-200 px-2 py-1">Signature</div>
                <div class="w-6 flex-shrink-0 ml-2"></div>
            </div>
            <toolbox-talk-attendee v-for="(attendee,index) in toolboxTalk.attendees"
                                   :key="attendee.id"
                                   :attendee="attendee"
                                   :toolbox-talk="toolboxTalk"
                                   :contacts="contacts"
                                   :readonly="true"
                                   ref="attendees"
            ></toolbox-talk-attendee>
        </div>
        <ui-data-grid>
            <ui-data-grid-item label="Given By" :value="toolboxTalk.givenByName"></ui-data-grid-item>
            <ui-data-grid-item label="Position" :value="toolboxTalk.givenByPosition"></ui-data-grid-item>
            <div class="lg:col-span-2">
                <dt class="text-sm font-medium text-gray-500">Signed</dt>
                <dd class="mt-1 text-sm text-gray-900 w-full">
                    <img v-if="toolboxTalk.signature" :src="toolboxTalk.signature" style="height:48px">
                    <div class="inline-block" v-else>
                        <ui-dropdown menu-class="p-3 w-56">
                            <template #default="{ toggle }">
                                <button @click="toggle"
                                        class="inline-block w-32 h-5 flex text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                    <i class="fa fa-ellipsis-h fa-fw mt-0.5"></i>
                                    <span class="inline-block whitespace-pre-wrap text-red-600 ml-2">NOT Signed</span>
                                </button>
                            </template>
                            <template #menu="{ close }">
                                <ui-dropdown-item icon="fas fa-pencil" @click="addSignature();close();">Add Signature
                                </ui-dropdown-item>
                            </template>
                        </ui-dropdown>
                    </div>
                </dd>
            </div>
        </ui-data-grid>

        <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend"></send-log>
        <ui-data-grid-item label="Quick action" wide>
            <div class="flex flex-col md:flex-row flex-wrap -m-2">
                <ui-button-new class="m-2" icon="fas fa-route" @click="duplicate" success>Duplicate
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-phone"
                               v-if="toolboxTalk.attendees.some((i)=> !i.signature && !i.signatureRequestedAt)"
                               @click="requestAllSignatures" extra>Request Signatures
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-file-pdf">
                    <a :href="toolboxTalk.getDownloadUrl()" tabindex="-1">Download PDF</a>
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-print" @click="print">Print PDF</ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-route" extra
                               @click="send"
                >Send
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid>
            <ui-data-grid-item label="Created By">{{ toolboxTalk.createdBy }} on {{
                    toolboxTalk.createdOn
                }}
            </ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="toolboxTalk.lastUpdatedBy">{{
                    toolboxTalk.lastUpdatedBy
                }} on {{
                    toolboxTalk.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>
</template>

<script>
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiContainer from "../../ui/UiContainer.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiHeader from "../../ui/UiHeader.vue";
import {uuid} from "../../../utils";
import {ToolboxTalk} from '@models'
import moment from "moment";
import SendLog from "../../send-log/SendLog.vue";
import ToolboxTalkAttendee from "./ToolboxTalkAttendee.vue";

export default {
    components: {
        ToolboxTalkAttendee,
        SendLog,
        UiHeader,
        UiDataGridItem,
        UiDataGrid,
        UiButtonNew, UiDropdown, UiDropdownItem, UiContainer, AutoresizableTextarea
    },
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
        addItem: Function,
        contacts: Array(),
        packageReference: null
    },
    computed: {
        combineSendLogs() {
            return this.toolboxTalk.sendLog.map((a) => {
                return a;
            }).concat(...this.toolboxTalk.attendees.map((a) => a.sendLog ? a.sendLog.map((b) => {
                return b;
            }) : []))
            .sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        }
    },
    data() {
        return {
            toolboxTalk: null,
        }
    },
    methods: {
        isAdmin() {
            return appData.user.is_admin
        },
        requestAllSignatures() {
            this.$modals.open('toolbox-talk-multiple-signature-request', {
                title: `Send signature request to all attendees not yet requested`,
                contacts: this.contacts,
                attendees: this.toolboxTalk.attendees,
                handle: async (data, modal) => {
                    let requests = [];
                    data.forEach((i) => {
                        if (i.to) {
                            requests.push(this.toolboxTalk.requestSignature(i.id, i.to))
                        }
                    })
                    await Promise.all(requests).then(async () => {
                        let ndb = await ToolboxTalk.find(this.toolboxTalk.packageReference, this.toolboxTalk.id)
                        Object.assign(this.toolboxTalk, ndb)
                        modal.close()
                    })
                }
            })
        },
        addSignature() {
            this.$modals.sign({
                title: `Sign as "${this.toolboxTalk.givenByName}"`,
                button: 'Confirmed',
                handle: (data) => {
                    this.toolboxTalk.signature = data.signature;
                    this.toolboxTalk.update();
                }
            })
        },
        openDeleteModal() {
            const title = 'Are you sure you want to remove Toolbox Talk ' + this.item.formattedDate + '?'
            const description = 'You are removing Toolbox Talk #' + this.item.id + '. Are you sure you want to do this?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
        async duplicate() {
            let data = this.toolboxTalk.getFormAttributes();
            data.attendees = this.toolboxTalk.attendees ? this.toolboxTalk.attendees : [];
            data.attendees.map((i) => {
                i.id = uuid(), i.signature = null
            })
            data.signature = null
            data.id = uuid();
            const toolboxTalk = await ToolboxTalk.create(this.packageReference, data.id, data);

            this.addItem(toolboxTalk, 0, (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            });
            this.edit(toolboxTalk);
        },
        print() {
            this.$emit('print', this.item.getPrintableUrl())
        },
        send() {
            this.$modals.open('toolbox-talk-send-modal', {
                title: `Send Toolbox Talk`,
                button: 'Send Toolbox Talk',
                toolboxTalk: this.toolboxTalk,
                handle: async (messageId, modal) => {
                    modal.close()
                    this.$nextTick(() => {
//                        window.Bus.emit('open-messaging', modal.to);
                    })
                }
            })
        },
        revoke(i) {
            this.toolboxTalk.revoke(i.id);
        },
        resend(i) {
            this.toolboxTalk.resend(i.id);
        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'ToolboxTalk') && (data.aggregate.id === this.toolboxTalk.id)) {
                    Object.assign(this.toolboxTalk, data.aggregate.data)
                    Object.assign(this.item, data.aggregate.data)
                }
            }
        },

    },
    async mounted() {
        this.toolboxTalk = await ToolboxTalk.find(this.item.packageReference, this.item.id)
        if (this.toolboxTalk) {
            User.registerAggregate({'type': 'ToolboxTalk', 'id': this.toolboxTalk.id}, this.updateFromListener)
        }
    }

}
</script>